import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { SearchTypeEnum } from '@enum/search.enum';

import { environment } from '@environments/environment';

import { PartyTypeEnum } from '@enum/party.enum';
import { PartyType, Party } from '@model/party.model';
import { SearchType } from '@model/search.model';

import { DEFUALT_NATIONALITY_CODE } from '@constant/default.constant';

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  constructor(private http: HttpClient) {}

  search(keywords: string, type: SearchTypeEnum, consentIds: string[]): Observable<Party[]> {
    if (!keywords || !type) {
      return of([]);
    }

    const paramName: string = this.getParamNameBySearchType(type);
    const partyType: PartyType = this.getPartyTypeBySearchType(type);
    const url: string = `${environment.endpoint}/parties`;

    return this.http.get<Party[]>(url + `?partyType=${partyType}&${paramName}=${keywords}&consentIds=${consentIds.toString()}`).pipe(
      take(1),
      catchError((_: HttpErrorResponse) => {
        return of([]);
      })
    );
  }

  getPartyTypeBySearchType(searchType: SearchTypeEnum): PartyType {
    switch (searchType) {
      case SearchTypeEnum.NATIONAL_ID:
        return PartyTypeEnum.PERSON;
      case SearchTypeEnum.PASSPORT_NO:
        return PartyTypeEnum.PERSON;
      case SearchTypeEnum.REGISTRATION_NO:
        return PartyTypeEnum.INSTITUTION;
      default:
        return PartyTypeEnum.PERSON;
    }
  }

  getParamNameBySearchType(searchType: SearchTypeEnum): SearchType {
    switch (searchType) {
      case SearchTypeEnum.NATIONAL_ID:
        return 'nationalId';
      case SearchTypeEnum.PASSPORT_NO:
        return 'passportNumber';
      case SearchTypeEnum.REGISTRATION_NO:
        return 'registrationNumber';
      default:
        return 'nationalId';
    }
  }

  validateSearchResult(searchResult: Party): string[] {
    let errors: string[] = [];

    if (!searchResult.customerSegmentCode) {
      errors = [...errors, 'customerSegment'];
    }

    return [...errors, ...this.validatePersonSearchResult(searchResult), ...this.validateInstitutionSearchResult(searchResult)];
  }

  private validatePersonSearchResult(searchResult: Party): string[] {
    let errors: string[] = [];

    if (searchResult.partyType === PartyTypeEnum.PERSON) {
      const isThaiNationality: boolean = searchResult.person.nationality === DEFUALT_NATIONALITY_CODE;

      const hasInvalidBirthDay: boolean = !searchResult.person.birthDate;
      const hasInvalidNationality: boolean = !searchResult.person.nationality;
      const hasInvalidThaiFirstname: boolean = !searchResult.person.firstNameTh;
      const hasInvalidThaiLastname: boolean = !searchResult.person.lastNameTh;
      const hasInvalidNationalID: boolean = !searchResult.person.nationalId || searchResult.person.nationalId.toString().length !== 13;

      if (hasInvalidBirthDay) {
        errors = [...errors, 'dob'];
      }

      if (hasInvalidNationality) {
        errors = [...errors, 'nationality'];
      }

      if (hasInvalidThaiFirstname) {
        errors = [...errors, 'firstNameTh'];
      }

      if (hasInvalidThaiLastname) {
        errors = [...errors, 'lastNameTh'];
      }

      if (isThaiNationality && hasInvalidNationalID) {
        errors = [...errors, 'nationalId'];
      }
    }

    return errors;
  }

  private validateInstitutionSearchResult(searchResult: Party): string[] {
    let errors: string[] = [];

    if (searchResult.partyType === PartyTypeEnum.INSTITUTION) {
      const hasInvalidInstitutionName: boolean = !searchResult.company.companyNameTh;
      const hasInvalidBranch: boolean = searchResult.company.mainBranch
        ? false
        : searchResult.company.branch.length !== 5 || isNaN(searchResult.company.branch as any);
      const hasInvalidBusinessType: boolean = !searchResult.company.businessTypeCode;

      if (hasInvalidInstitutionName) {
        errors = [...errors, 'institutionName'];
      }

      if (hasInvalidBranch) {
        errors = [...errors, 'branch'];
      }

      if (hasInvalidBusinessType) {
        errors = [...errors, 'typeOfBussiness'];
      }
    }

    return errors;
  }
}
